import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Clock,
  XCircle,
  TrendingUp,
  AlertTriangle,
  CalendarDays,
  Download,
} from "lucide-react";
import { format } from "date-fns";
import {
  getWeeklySalesReport,
  type WeeklySalesReportResponse,
  type WeeklySalesReportItem,
  type WeeklySalesReportSection,
} from "@/lib/dashboardApi";
import { downloadWeeklySalesReportPdf } from "@/lib/weeklySalesReportPdf";

const STALE_FOLLOW_UP_DAYS = 5;

const formatMoney = (value?: number | null, currency?: string | null) => {
  const amount = typeof value === "number" ? value : 0;
  const cur = (currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "MMM d, yyyy");
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "MMM d, yyyy 'at' HH:mm");
};

const toYmd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const WeeklySalesReport = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<WeeklySalesReportResponse | null>(null);
  // `weekRef` is any date inside the target week; undefined = current week.
  const [weekRef, setWeekRef] = useState<string | undefined>(undefined);

  const load = async (ref?: string) => {
    try {
      setLoading(true);
      const data = await getWeeklySalesReport(ref);
      setReport(data);
    } catch (e: any) {
      toast({ title: "Could not load the report", description: e?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(weekRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekRef]);

  // Monday (00:00) of the current calendar week — the latest week the report may show.
  const currentWeekStart = useMemo(() => {
    const d = new Date();
    const dayFromMonday = (d.getDay() + 6) % 7;
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - dayFromMonday);
    return toYmd(d);
  }, []);

  const atCurrentWeek = !report || report.week_start >= currentWeekStart;

  const shiftWeek = (deltaDays: number) => {
    const base = report?.week_start ? new Date(`${report.week_start}T00:00:00`) : new Date();
    base.setDate(base.getDate() + deltaDays);
    // Never navigate into a future week — clamp forward moves to the current week.
    if (toYmd(base) >= currentWeekStart) {
      setWeekRef(undefined);
      return;
    }
    setWeekRef(toYmd(base));
  };

  const grandTotal = useMemo(() => {
    if (!report) return 0;
    return (report.new_proposals.total_value || 0) + (report.older_active_proposals.total_value || 0);
  }, [report]);

  if (loading && !report) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!report) {
    return (
      <DashboardLayout>
        <div className="text-center py-12 text-muted-foreground">No report data available.</div>
      </DashboardLayout>
    );
  }

  const weekLabel = `${formatDate(report.week_start)} – ${formatDate(report.week_end)}`;

  const ItemsTable = ({
    items,
    variant,
  }: {
    items: WeeklySalesReportItem[];
    variant: "new" | "followup" | "canceled";
  }) => {
    if (items.length === 0) {
      return <div className="px-4 py-6 text-sm text-muted-foreground text-center">No proposals in this group.</div>;
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-medium">{variant === "canceled" ? "Canceled" : "Date"}</th>
              <th className="px-3 py-2 font-medium">Proposal</th>
              <th className="px-3 py-2 font-medium">Owner</th>
              {variant === "followup" && <th className="px-3 py-2 font-medium">Last follow-up</th>}
              {variant === "canceled" && <th className="px-3 py-2 font-medium">Type</th>}
              <th className="px-3 py-2 font-medium text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const stale =
                variant === "followup" &&
                typeof item.days_since_last_follow_up === "number" &&
                item.days_since_last_follow_up > STALE_FOLLOW_UP_DAYS;
              return (
                <tr
                  key={item.id}
                  className={`border-b border-border/40 last:border-0 transition-colors hover:bg-muted/40 ${
                    stale ? "bg-red-50/50" : ""
                  }`}
                >
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">
                    {variant === "canceled" ? formatDate(item.canceled_at) : formatDate(item.created_at)}
                  </td>
                  <td className="px-3 py-2.5">
                    <Link to={`/proposals/${item.id}`} className="font-medium text-foreground hover:text-primary hover:underline">
                      {item.title || "Untitled"}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {item.internal_reference ? `${item.internal_reference} · ` : ""}
                      {item.client_name && item.client_name !== "Unknown" ? item.client_name : ""}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">{item.owner_name || "—"}</td>
                  {variant === "followup" && (
                    <td className="px-3 py-2.5 align-top max-w-[300px]">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className={stale ? "font-semibold text-red-700" : "text-foreground"}>
                          {item.last_follow_up_at ? formatDateTime(item.last_follow_up_at) : "Never"}
                        </span>
                        {typeof item.days_since_last_follow_up === "number" && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            stale ? "bg-red-600 text-white" : "bg-muted text-muted-foreground"
                          }`}>
                            {stale && <AlertTriangle className="h-2.5 w-2.5" />}
                            {item.days_since_last_follow_up}d
                          </span>
                        )}
                      </div>
                      {item.last_follow_up_message && (
                        <p
                          className="mt-0.5 text-xs text-muted-foreground italic line-clamp-2 break-words"
                          title={item.last_follow_up_message}
                        >
                          “{item.last_follow_up_message}”
                        </p>
                      )}
                    </td>
                  )}
                  {variant === "canceled" && (
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {item.cancellation_type === "external" ? (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">External</Badge>
                      ) : (
                        <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-800">Internal</Badge>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2.5 whitespace-nowrap text-right font-semibold">
                    {formatMoney(item.value, item.currency)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const SectionHeader = ({
    icon,
    title,
    count,
    total,
    tone,
  }: {
    icon: React.ReactNode;
    title: string;
    count: number;
    total?: number;
    tone: string;
  }) => (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <CardTitle className="flex items-center gap-2 text-lg">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>{icon}</span>
        {title}
        <Badge variant="outline">{count}</Badge>
      </CardTitle>
      {typeof total === "number" && (
        <span className="text-sm text-muted-foreground">
          Total: <span className="font-semibold text-foreground">{formatMoney(total, "USD")}</span>
        </span>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="mx-auto w-full max-w-[1200px] space-y-6 pb-4">
        {/* Header */}
        <Card className="border-border/70 bg-gradient-to-br from-primary/5 via-card to-card shadow-sm">
          <CardContent className="p-5 md:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-primary" />
                  Weekly Sales Report
                </h1>
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" />
                  {weekLabel}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => shiftWeek(-7)} className="gap-1">
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setWeekRef(undefined)} disabled={atCurrentWeek}>
                  This week
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => shiftWeek(7)}
                  disabled={atCurrentWeek}
                  className="gap-1"
                  title={atCurrentWeek ? "You're on the current week" : undefined}
                >
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => downloadWeeklySalesReportPdf(report)}
                  className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard tone="bg-emerald-100 text-emerald-700" icon={<Sparkles className="h-5 w-5" />}
            label="New this week" value={String(report.new_proposals.count)}
            sub={formatMoney(report.new_proposals.total_value, "USD")} />
          <KpiCard tone="bg-blue-100 text-blue-700" icon={<Clock className="h-5 w-5" />}
            label="Open (older) pipeline" value={String(report.older_active_proposals.count)}
            sub={formatMoney(report.older_active_proposals.total_value, "USD")} />
          <KpiCard tone="bg-rose-100 text-rose-700" icon={<XCircle className="h-5 w-5" />}
            label="Canceled this week" value={String(report.canceled.count)}
            sub={`${report.canceled.internal.count} internal · ${report.canceled.external.count} external`} />
          <KpiCard tone="bg-primary/15 text-primary" icon={<TrendingUp className="h-5 w-5" />}
            label="Pipeline value" value={formatMoney(grandTotal, "USD")}
            sub="New + open proposals" />
        </div>

        {/* New proposals */}
        <Card className="border-border/70 shadow-sm">
          <CardHeader className="border-b border-border/60 pb-3">
            <SectionHeader icon={<Sparkles className="h-4 w-4" />} tone="bg-emerald-100 text-emerald-700"
              title="New proposals this week" count={report.new_proposals.count} total={report.new_proposals.total_value} />
          </CardHeader>
          <CardContent className="p-0">
            <ItemsTable items={report.new_proposals.items} variant="new" />
          </CardContent>
        </Card>

        {/* Follow-up watch */}
        <Card className="border-border/70 shadow-sm">
          <CardHeader className="border-b border-border/60 pb-3">
            <SectionHeader icon={<Clock className="h-4 w-4" />} tone="bg-blue-100 text-blue-700"
              title="Follow-up watch (older open proposals)" count={report.older_active_proposals.count}
              total={report.older_active_proposals.total_value} />
          </CardHeader>
          <CardContent className="p-0">
            <ItemsTable items={report.older_active_proposals.items} variant="followup" />
          </CardContent>
        </Card>

        {/* Cancellations */}
        <Card className="border-border/70 shadow-sm">
          <CardHeader className="border-b border-border/60 pb-3">
            <SectionHeader icon={<XCircle className="h-4 w-4" />} tone="bg-rose-100 text-rose-700"
              title="Cancellations this week" count={report.canceled.count} total={report.canceled.total_value} />
          </CardHeader>
          <CardContent className="p-4 space-y-5">
            <div>
              <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold">
                <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-800">Internal interference</Badge>
                <span className="text-muted-foreground">
                  {report.canceled.internal.count} · {formatMoney(report.canceled.internal.total_value, "USD")}
                </span>
              </div>
              <div className="rounded-lg border border-border/60">
                <ItemsTable items={report.canceled.internal.items} variant="canceled" />
              </div>
            </div>
            <div>
              <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold">
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">External interference</Badge>
                <span className="text-muted-foreground">
                  {report.canceled.external.count} · {formatMoney(report.canceled.external.total_value, "USD")}
                </span>
              </div>
              <div className="rounded-lg border border-border/60">
                <ItemsTable items={report.canceled.external.items} variant="canceled" />
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Generated {formatDate(report.generated_at)} · High Class Travel Experiences
        </p>
      </div>
    </DashboardLayout>
  );
};

const KpiCard = ({
  tone,
  icon,
  label,
  value,
  sub,
}: {
  tone: string;
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) => (
  <Card className="border-border/70 shadow-sm">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</span>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground truncate">{label}</p>
          <p className="text-xl font-bold leading-tight">{value}</p>
          {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default WeeklySalesReport;
